import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { WavyDivider } from '../WavyDivider';

/**
 * BLOCK: cta_banner
 * ACF layout name: `block_cta_banner`
 *
 * Fields:
 *  - eyebrow      (text)
 *  - heading      (text)
 *  - body         (textarea, optional)
 *  - button_label (text)
 *  - button_link  (page link)
 *  - divider_from (colour picker — the background of the section above; set to
 *                  "none" when the previous block already renders a divider)
 *
 * Consolidates the closing "A gentle next step" band that appeared, hand-rolled,
 * on Home, Start Your Journey, Meet Milangela, Journal, Guided Meditations and
 * Podcasts — six near-identical copies reduced to one.
 */

interface CtaBannerProps {
  eyebrow?: string;
  heading: string;
  body?: string;
  buttonLabel: string;
  buttonTo: string;
  /** Background colour of the section above; renders the wave transition. */
  dividerFrom?: string | 'none';
}
export function CtaBanner({
  eyebrow = 'A gentle next step',
  heading,
  body,
  buttonLabel,
  buttonTo,
  dividerFrom = 'none'
}: CtaBannerProps) {
  return <>
      {dividerFrom !== 'none' && <div className="pointer-events-none relative" style={{
      backgroundColor: dividerFrom
    }} data-id="element-62" data-mp="ic577p64n4ur">
          <WavyDivider fill="#3A3A38" data-id="element-63" data-mp="dc92totipbeo" />
        </div>}
      <section className="bg-charcoal px-6 pb-24 pt-8 text-center md:pb-28" data-id="element-64" data-mp="8e7k55007e3q">
        <div className="mx-auto max-w-3xl" data-id="element-65" data-mp="8gbykgvj3vi9">
          {eyebrow && <p className="text-xs font-medium tracking-[0.28em] text-rose uppercase" data-id="element-66" data-mp="3mpjyj5t1hjt">
              {eyebrow}
            </p>}
          <h2 className="mt-5 font-serif text-4xl leading-tight text-cream sm:text-5xl" data-id="element-67" data-mp="3voxh2kcasj8y">
            {heading}
          </h2>
          {body && <p className="mx-auto mt-6 max-w-2xl text-base font-light leading-relaxed text-cream-100/75 sm:text-lg" data-id="element-68" data-mp="3ls2jq4rrgow9">
              {body}
            </p>}
          <Link to={buttonTo} className="mt-9 inline-flex items-center gap-3 border border-cream px-7 py-3.5 text-xs font-medium tracking-[0.16em] text-cream uppercase transition-colors hover:bg-cream hover:text-charcoal focus:outline-none focus:ring-2 focus:ring-cream focus:ring-offset-2 focus:ring-offset-charcoal" data-id="element-69" data-mp="376eyshdqhgcc">
            {buttonLabel}
            <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" data-id="element-70" data-mp="329euoc0g2nnv" />
          </Link>
        </div>
      </section>
    </>;
}